import { useQuery } from "@tanstack/react-query";
import { Mail, MapPin, Phone, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { getCompany } from "@/lib/api";

export default function Contact() {
  const { data: company } = useQuery({
    queryKey: ["company"],
    queryFn: getCompany,
  });

  return (
    <div data-testid="page-contact" className="pb-12">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <p
          data-testid="contact-eyebrow"
          className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold"
        >
          Get in touch
        </p>
        <h1
          data-testid="contact-title"
          className="mt-3 font-heading text-5xl sm:text-6xl tracking-tight font-light text-slate-950 max-w-3xl"
        >
          We&apos;d love to hear from you.
        </h1>
        <p className="mt-6 max-w-xl text-slate-600 leading-relaxed">
          Visit our showroom, call us, or reach out on social. Our team is happy
          to walk you through the lineup, answer questions and help you pick the
          model that fits your ride.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href={`tel:${(company?.phone || "").replace(/\s+/g, "")}`}
            data-testid="contact-phone-card"
            className="rounded-3xl border border-slate-200 bg-white p-7 hover:border-slate-950 transition-colors"
          >
            <Phone className="h-5 w-5 text-emerald-600" />
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">Call</p>
            <p className="mt-1 font-heading text-xl text-slate-950">
              {company?.phone || "—"}
            </p>
          </a>
          <a
            href={`mailto:${company?.email || ""}`}
            data-testid="contact-email-card"
            className="rounded-3xl border border-slate-200 bg-white p-7 hover:border-slate-950 transition-colors"
          >
            <Mail className="h-5 w-5 text-emerald-600" />
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">Write</p>
            <p className="mt-1 font-heading text-xl text-slate-950 break-all">
              {company?.email || "—"}
            </p>
          </a>
          <div
            data-testid="contact-address-card"
            className="sm:col-span-2 rounded-3xl border border-slate-200 bg-white p-7"
          >
            <MapPin className="h-5 w-5 text-emerald-600" />
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">Showroom</p>
            <p className="mt-1 font-heading text-xl text-slate-950">
              {company?.address || "—"}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Open Mon–Sat · 10:00 to 19:00
            </p>
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="rounded-3xl bg-slate-950 text-white p-10 h-full flex flex-col">
            <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-semibold">
              Follow along
            </p>
            <h2 className="mt-3 font-heading text-3xl tracking-tight">
              See the lineup in motion.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Behind-the-scenes builds, ride stories and customer journeys — all
              on our social channels.
            </p>
            <div className="mt-auto pt-10 flex items-center gap-3">
              <a
                aria-label="Instagram"
                data-testid="contact-instagram"
                href={company?.social?.instagram || "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 hover:border-emerald-500 hover:text-white"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                aria-label="Facebook"
                data-testid="contact-facebook"
                href={company?.social?.facebook || "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 hover:border-emerald-500 hover:text-white"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                aria-label="YouTube"
                data-testid="contact-youtube"
                href={company?.social?.youtube || "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 hover:border-emerald-500 hover:text-white"
              >
                <Youtube className="h-4 w-4" />
              </a>
              <a
                aria-label="LinkedIn"
                data-testid="contact-linkedin"
                href={company?.social?.linkedin || "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 hover:border-emerald-500 hover:text-white"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
